// ============================================================
// LibraryModal
// ============================================================

import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useLibraryStore } from '../../store/libraryStore';
import { useSongStore } from '../../store/songStore';
import type { SongMeta, Folder } from '../../core/library';
import type { Song } from '../../core/types';

// ------------------------------------------------------------
// CONFIRMATION DIALOG
// ------------------------------------------------------------

interface ConfirmDialogProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ message, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div className="bg-paper border border-rule rounded shadow-dropdown p-6 max-w-sm w-full mx-4">
        <p className="font-mono text-sm text-ink-primary mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="metadata-badge hover:bg-hover">Cancel</button>
          <button onClick={onConfirm} className="metadata-badge border-error text-error hover:bg-error/10">Delete</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// TRASH ICON
// ------------------------------------------------------------

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.75 3.5h10.5" />
      <path d="M5.25 3.5V2.333A.583.583 0 015.833 1.75h2.334a.583.583 0 01.583.583V3.5" />
      <path d="M11.083 3.5l-.583 8.167a.583.583 0 01-.583.583H4.083a.583.583 0 01-.583-.583L2.917 3.5" />
      <path d="M5.833 6.417v3.5M8.167 6.417v3.5" />
    </svg>
  );
}

// ------------------------------------------------------------
// SHARED BADGE
// Small indicator shown on songs that are shared with others
// or received from another user.
// ------------------------------------------------------------

function SharedBadge({ received }: { received?: boolean }) {
  return (
    <div
      title={received ? 'Shared with you' : 'Shared'}
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        gap:            3,
        padding:        '1px 5px',
        borderRadius:   999,
        border:         '1px solid var(--color-accent)',
        color:          'var(--color-accent)',
        fontFamily:     'var(--font-mono)',
        fontSize:       9,
        letterSpacing:  '0.05em',
        lineHeight:     1.6,
        flexShrink:     0,
        opacity:        0.85,
      }}
    >
      {/* Simple two-person icon */}
      <svg width="9" height="9" viewBox="0 0 12 12" fill="currentColor">
        <circle cx="4.5" cy="3.5" r="2" />
        <path d="M0 10c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
        <circle cx="9" cy="3" r="1.5" opacity="0.6" />
        <path d="M9 6.5c1.5 0 3 .8 3 2.5" opacity="0.6" />
      </svg>
      {received ? 'Shared' : 'Shared'}
    </div>
  );
}

// ------------------------------------------------------------
// SONG ROW
// ------------------------------------------------------------

interface SongRowProps {
  song: SongMeta;
  isActive: boolean;
  onOpen: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isDragging?: boolean;
}

function SongRow({ song, isActive, onOpen, onRename, onDuplicate, onDelete, isDragging }: SongRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: song.id, data: { type: 'song', folderId: song.folderId } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const [showMenu, setShowMenu]         = useState(false);
  const [trashConfirm, setTrashConfirm] = useState(false);
  const menuRef    = useRef<HTMLDivElement>(null);
  const trashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!showMenu) return;
    const handle = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showMenu]);

  const handleMouseLeave = () => {
    setTrashConfirm(false);
    if (trashTimer.current) clearTimeout(trashTimer.current);
  };

  const handleTrashClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (trashConfirm) {
      if (trashTimer.current) clearTimeout(trashTimer.current);
      onDelete();
      setTrashConfirm(false);
    } else {
      setTrashConfirm(true);
      trashTimer.current = setTimeout(() => setTrashConfirm(false), 3000);
    }
  };

  const date = new Date(song.updatedAt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  // A song is "shared" if it was received from someone else (sharedWith is set)
  const isShared = !!song.sharedWith;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        'library-song-item flex items-center gap-2 px-3 py-2.5 rounded group/song transition-colors',
        isActive
          ? 'bg-accent/10 border border-accent/30'
          : 'hover:bg-hover border border-transparent',
      ].join(' ')}
      onMouseLeave={handleMouseLeave}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="text-ink-muted opacity-0 group-hover/song:opacity-60 transition-opacity cursor-grab active:cursor-grabbing select-none touch-none flex-shrink-0"
        style={{ fontSize: 14, lineHeight: 1 }}
      >⠿</div>

      {/* Info */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div className={[
            'font-display font-semibold truncate leading-tight',
            isActive ? 'text-accent' : 'text-ink-primary',
          ].join(' ')} style={{ fontSize: 15 }}>
            {song.title}
          </div>
          {/* FIX: shared badge — shown when the song was received from another user */}
          {isShared && <SharedBadge received />}
        </div>
        <div className="font-mono text-ink-muted" style={{ fontSize: 10, marginTop: 1 }}>
          {date}
        </div>
      </div>

      {/* Inline trash */}
      <div className="relative flex-shrink-0">
        <button
          onClick={handleTrashClick}
          title={trashConfirm ? 'Click again to confirm delete' : 'Delete chart'}
          className={[
            'control-btn opacity-0 group-hover/song:opacity-100 transition-opacity flex items-center justify-center',
            trashConfirm ? 'text-error opacity-100' : 'hover:text-error',
          ].join(' ')}
          style={{ width: 26, height: 26 }}
        >
          <TrashIcon />
        </button>
        {trashConfirm && (
          <div style={{
            position: 'absolute', right: 30, top: '50%', transform: 'translateY(-50%)',
            whiteSpace: 'nowrap', background: 'var(--color-paper)',
            border: '1px solid var(--color-error)', borderRadius: 4,
            padding: '3px 8px', fontSize: 10, fontFamily: 'var(--font-mono)',
            color: 'var(--color-error)', pointerEvents: 'none', zIndex: 10,
          }}>
            Click again to delete
          </div>
        )}
      </div>

      {/* ··· menu */}
      <div className="relative flex-shrink-0" ref={menuRef}>
        <button
          onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); }}
          className="control-btn opacity-0 group-hover/song:opacity-100 transition-opacity"
          style={{ fontSize: 11, letterSpacing: 1 }}
        >···</button>
        {showMenu && (
          <div className="dropdown absolute right-0 top-full mt-1 py-1 w-36" style={{ zIndex: 600 }}>
            {([
              { label: 'Open',      fn: onOpen },
              { label: 'Rename',    fn: onRename },
              { label: 'Duplicate', fn: onDuplicate },
            ] as Array<{ label: string; fn: () => void }>).map((item) => (
              <button
                key={item.label}
                onClick={() => { item.fn(); setShowMenu(false); }}
                className="w-full text-left px-3 py-1.5 text-sm font-mono transition-colors text-ink-secondary hover:bg-hover"
              >{item.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// FOLDER SECTION
// ------------------------------------------------------------

interface FolderSectionProps {
  folder: Folder | null;
  songs: SongMeta[];
  activeSongId: string | null;
  draggedSongId: string | null;
  onOpenSong: (id: string) => void;
  onRenameSong: (id: string) => void;
  onDuplicateSong: (id: string) => void;
  onDeleteSong: (id: string) => void;
  onDeleteFolder?: () => void;
  onCreateSong: (folderId: string | null) => void;
}

function FolderSection({
  folder, songs, activeSongId, draggedSongId,
  onOpenSong, onRenameSong, onDuplicateSong, onDeleteSong,
  onDeleteFolder, onCreateSong,
}: FolderSectionProps) {
  const renameFolderStore = useLibraryStore((s) => s.renameFolder);
  const [collapsed, setCollapsed]     = useState(false);
  const [renaming, setRenaming]       = useState(false);
  const [nameDraft, setNameDraft]     = useState(folder?.name ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  const droppableId = folder ? `folder-${folder.id}` : 'folder-unfiled';
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: droppableId });

  useEffect(() => { setNameDraft(folder?.name ?? ''); }, [folder?.name]);
  useEffect(() => { if (renaming) inputRef.current?.focus(); }, [renaming]);

  const commitRename = () => {
    if (folder && nameDraft.trim()) renameFolderStore(folder.id, nameDraft.trim());
    setRenaming(false);
  };

  return (
    <div
      ref={setDropRef}
      style={{
        marginBottom: 12, borderRadius: 6,
        border: isOver ? '1px dashed var(--color-accent)' : '1px solid transparent',
        background: isOver ? 'rgba(var(--color-accent-rgb), 0.04)' : 'transparent',
        transition: 'border-color 120ms, background 120ms',
      }}
    >
      {/* Folder header */}
      <div className="library-folder-row flex items-center gap-1.5 px-1 mb-1 group/folder" style={{ minHeight: 24 }}>
        <button
          onClick={() => setCollapsed((v) => !v)}
          style={{
            fontSize: 10, color: 'var(--color-ink-muted)',
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
            transition: 'transform 120ms',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >▾</button>

        {renaming ? (
          <input
            ref={inputRef}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  commitRename();
              if (e.key === 'Escape') { setNameDraft(folder?.name ?? ''); setRenaming(false); }
            }}
            className="font-label tracking-widest uppercase text-ink-secondary bg-transparent border-b border-accent outline-none flex-1"
            style={{ fontSize: 10 }}
          />
        ) : (
          <span
            className="font-label tracking-widest uppercase text-ink-secondary flex-1"
            style={{ fontSize: 10 }}
            onDoubleClick={() => folder && setRenaming(true)}
          >
            {folder?.name ?? 'Unfiled'}
            <span className="font-mono normal-case text-ink-muted" style={{ letterSpacing: 0, marginLeft: 6, fontSize: 10 }}>
              {songs.length}
            </span>
          </span>
        )}

        <div className="opacity-0 group-hover/folder:opacity-100 transition-opacity flex gap-1">
          {folder && (
            <>
              <button onClick={() => setRenaming(true)} className="control-btn" title="Rename" style={{ fontSize: 10 }}>✏</button>
              <button onClick={onDeleteFolder} className="control-btn text-error" title="Delete folder" style={{ fontSize: 10 }}>✕</button>
            </>
          )}
          <button onClick={() => onCreateSong(folder?.id ?? null)} className="control-btn" title="New song here" style={{ fontSize: 12 }}>+</button>
        </div>
      </div>

      {/* Songs */}
      {!collapsed && (
        <SortableContext items={songs.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div style={{ paddingLeft: 16 }}>
            {songs.length === 0 ? (
              <div className="font-mono text-ink-muted text-center py-2" style={{ fontSize: 11, opacity: 0.4 }}>Empty</div>
            ) : (
              songs.map((song) => (
                <SongRow
                  key={song.id}
                  song={song}
                  isActive={activeSongId === song.id}
                  onOpen={() => onOpenSong(song.id)}
                  onRename={() => onRenameSong(song.id)}
                  onDuplicate={() => onDuplicateSong(song.id)}
                  onDelete={() => onDeleteSong(song.id)}
                  isDragging={draggedSongId === song.id}
                />
              ))
            )}
          </div>
        </SortableContext>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// MAIN COMPONENT
// ------------------------------------------------------------

interface LibraryModalProps {
  onSongLoad: (song: Song) => void;
}

export function LibraryModal({ onSongLoad }: LibraryModalProps) {
  const isOpen          = useLibraryStore((s) => s.isOpen);
  const closeLibrary    = useLibraryStore((s) => s.closeLibrary);
  const index           = useLibraryStore((s) => s.index);
  const searchQuery     = useLibraryStore((s) => s.searchQuery);
  const setSearch       = useLibraryStore((s) => s.setSearchQuery);
  const activeSongId    = useLibraryStore((s) => s.activeSongId);
  const getFiltered     = useLibraryStore((s) => s.getFilteredSongs);
  const refreshIndex    = useLibraryStore((s) => s.refreshIndex);
  const openSong        = useLibraryStore((s) => s.openSong);
  const createSong      = useLibraryStore((s) => s.createSong);
  const duplicateSong   = useLibraryStore((s) => s.duplicateSong);
  const renameSongStore = useLibraryStore((s) => s.renameSong);
  const deleteSongStore = useLibraryStore((s) => s.deleteSong);
  const moveSong        = useLibraryStore((s) => s.moveSong);
  const createFolderStore = useLibraryStore((s) => s.createFolder);
  const deleteFolderStore = useLibraryStore((s) => s.deleteFolder);
  const saveCurrentSong   = useLibraryStore((s) => s.saveCurrentSong);
  const currentSong       = useSongStore((s) => s.song);

  const [confirm, setConfirm]               = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [renamingId, setRenamingId]         = useState<string | null>(null);
  const [renameDraft, setRenameDraft]       = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName]   = useState('');
  const [draggedSongId, setDraggedSongId]   = useState<string | null>(null);

  const renameInputRef = useRef<HTMLInputElement>(null);
  const newFolderRef   = useRef<HTMLInputElement>(null);

  useEffect(() => { if (renamingId) renameInputRef.current?.focus(); }, [renamingId]);
  useEffect(() => { if (creatingFolder) newFolderRef.current?.focus(); }, [creatingFolder]);

  useEffect(() => {
    if (!isOpen) return;
    saveCurrentSong(currentSong);
    refreshIndex();
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') closeLibrary(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  if (!isOpen) return null;

  const filteredSongs = getFiltered();
  const folders       = [...index.folders].sort((a, b) => a.order - b.order);
  const songsByFolder: Record<string, SongMeta[]> = { unfiled: [] };
  folders.forEach((f) => { songsByFolder[f.id] = []; });
  filteredSongs.forEach((s) => {
    const key = s.folderId ?? 'unfiled';
    if (!songsByFolder[key]) songsByFolder[key] = [];
    songsByFolder[key].push(s);
  });

  function handleDragStart(e: DragStartEvent) { setDraggedSongId(e.active.id as string); }
  function handleDragEnd(e: DragEndEvent) {
    setDraggedSongId(null);
    const { active, over } = e;
    if (!over) return;
    const activeId = active.id as string;
    const overId   = over.id as string;
    if (overId === 'folder-unfiled') { moveSong(activeId, null); return; }
    if (overId.startsWith('folder-')) {
      const folderId = overId.replace('folder-', '');
      const folder = index.folders.find((f) => f.id === folderId);
      if (folder) { moveSong(activeId, folder.id); return; }
    }
    if (activeId !== overId) {
      const targetSong = index.songs.find((s) => s.id === overId);
      if (targetSong) moveSong(activeId, targetSong.folderId);
    }
  }

  const handleOpen      = (id: string) => { saveCurrentSong(currentSong); openSong(id, onSongLoad); };
  const handleCreate    = (folderId: string | null = null) => { saveCurrentSong(currentSong); createSong(onSongLoad, folderId); };
  const handleDuplicate = (id: string) => { duplicateSong(id, onSongLoad); };
  const handleDeleteDirect = (id: string) => { deleteSongStore(id); };
  const handleDelete = (id: string) => {
    const s = index.songs.find((x) => x.id === id);
    setConfirm({ message: `Delete "${s?.title ?? 'this chart'}"? This cannot be undone.`, onConfirm: () => { deleteSongStore(id); setConfirm(null); } });
  };
  void handleDelete;
  const handleDeleteFolder = (id: string) => {
    const f = index.folders.find((x) => x.id === id);
    const n = index.songs.filter((s) => s.folderId === id).length;
    setConfirm({ message: `Delete folder "${f?.name}"?${n > 0 ? ` ${n} song(s) will move to Unfiled.` : ''} Cannot be undone.`, onConfirm: () => { deleteFolderStore(id); setConfirm(null); } });
  };
  const handleRename    = (id: string) => { const s = index.songs.find((x) => x.id === id); setRenamingId(id); setRenameDraft(s?.title ?? ''); };
  const commitRename    = () => { if (renamingId && renameDraft.trim()) renameSongStore(renamingId, renameDraft.trim()); setRenamingId(null); };
  const handleNewFolder = () => { if (newFolderName.trim()) createFolderStore(newFolderName.trim()); setCreatingFolder(false); setNewFolderName(''); };

  const drawerWidth = Math.min(400, typeof window !== 'undefined' ? window.innerWidth : 400);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeLibrary}
        style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }}
      />

      <div
        onClick={(e) => e.stopPropagation()}
        className="library-drawer"
        style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: drawerWidth, zIndex: 500,
          background: 'var(--color-paper)',
          borderRight: '1px solid var(--color-rule)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '4px 0 40px rgba(0,0,0,0.18)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--color-rule)', flexShrink: 0 }}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-label tracking-widest uppercase text-ink-secondary" style={{ fontSize: 11 }}>
              My Charts
            </span>
            <div className="flex gap-2 items-center">
              <button onClick={() => handleCreate()} className="metadata-badge" title="New chart">+ New</button>
              <button onClick={() => setCreatingFolder(true)} className="metadata-badge" title="New folder">+ Folder</button>
              <button onClick={closeLibrary} className="library-drawer-close control-btn" title="Close">✕</button>
            </div>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--color-ink-muted)', pointerEvents: 'none' }}>⌕</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search charts…"
              className="library-search"
              style={{
                width: '100%', fontFamily: 'var(--font-mono)', fontSize: 13,
                padding: '7px 30px 7px 30px',
                background: 'var(--color-paper-alt)', border: '1px solid var(--color-rule)',
                borderRadius: 6, outline: 'none', color: 'var(--color-ink-primary)',
              }}
            />
            {searchQuery && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--color-ink-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
            )}
          </div>

          {creatingFolder && (
            <div style={{ marginTop: 8 }}>
              <input
                ref={newFolderRef}
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onBlur={handleNewFolder}
                onKeyDown={(e) => { if (e.key === 'Enter') handleNewFolder(); if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); } }}
                placeholder="Folder name…"
                style={{
                  width: '100%', fontFamily: 'var(--font-label)', fontSize: 10,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  padding: '5px 10px', background: 'var(--color-paper-alt)',
                  border: '1px solid var(--color-accent)', borderRadius: 4,
                  outline: 'none', color: 'var(--color-ink-primary)',
                }}
              />
            </div>
          )}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            {folders.map((folder) => (
              <FolderSection
                key={folder.id}
                folder={folder}
                songs={songsByFolder[folder.id] ?? []}
                activeSongId={activeSongId}
                draggedSongId={draggedSongId}
                onOpenSong={handleOpen}
                onRenameSong={handleRename}
                onDuplicateSong={handleDuplicate}
                onDeleteSong={handleDeleteDirect}
                onDeleteFolder={() => handleDeleteFolder(folder.id)}
                onCreateSong={handleCreate}
              />
            ))}
            <FolderSection
              folder={null}
              songs={songsByFolder['unfiled'] ?? []}
              activeSongId={activeSongId}
              draggedSongId={draggedSongId}
              onOpenSong={handleOpen}
              onRenameSong={handleRename}
              onDuplicateSong={handleDuplicate}
              onDeleteSong={handleDeleteDirect}
              onCreateSong={handleCreate}
            />
            <DragOverlay>
              {draggedSongId && (() => {
                const s = index.songs.find((x) => x.id === draggedSongId);
                return s ? (
                  <div style={{ background: 'var(--color-paper)', border: '1px solid var(--color-accent)', borderRadius: 6, padding: '8px 14px', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--color-accent)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', cursor: 'grabbing' }}>
                    {s.title}
                  </div>
                ) : null;
              })()}
            </DragOverlay>
          </DndContext>

          {filteredSongs.length === 0 && (
            <div className="text-center py-12 text-ink-muted font-mono" style={{ fontSize: 13 }}>
              {searchQuery ? `No charts matching "${searchQuery}"` : 'No charts yet — create one above'}
            </div>
          )}
        </div>

        {/* Rename song dialog */}
        {renamingId && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
            <div className="bg-paper border border-rule rounded shadow-dropdown p-5 w-72">
              <div className="font-label tracking-widest uppercase text-ink-muted mb-3" style={{ fontSize: 9 }}>Rename chart</div>
              <input
                ref={renameInputRef}
                type="text"
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingId(null); }}
                className="song-title-input text-xl w-full mb-4"
                spellCheck={false}
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setRenamingId(null)} className="metadata-badge">Cancel</button>
                <button onClick={commitRename} className="metadata-badge border-accent text-accent hover:bg-accent/10">Rename</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {confirm && <ConfirmDialog message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
    </>
  );
}